import React from 'react';

const RELOAD_GUARD_KEY = 'dc-chunk-reload';

// Webpack rebuilds the route chunks during HMR / fast refresh. If a route
// transition happens after a rebuild but before the browser has fetched the
// new manifest, the lazy import fails with "Loading chunk N failed" (or its
// modern variant "Failed to fetch dynamically imported module"). The fix is
// a one-shot page reload — reload pulls a fresh chunk-manifest, and the
// session flag stops the loop if the failure is real.
const isChunkLoadError = (err) => {
  const message = String(err?.message || '');
  const name = String(err?.name || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;
    try {
      if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return; // already retried this session — let the fallback render
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      // sessionStorage may be blocked; reload anyway, worst case is a loop the user can break
    }
    window.location.reload();
  }

  componentDidUpdate(_, prevState) {
    // Successful render after recovering — clear the guard so a future stale
    // chunk can trigger a reload again later in the same tab.
    if (prevState.error && !this.state.error) {
      try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* noop */ }
    }
  }

  reset = () => {
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* noop */ }
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (isChunkLoadError(error)) {
      // Boundary already triggered a reload in componentDidCatch; render
      // nothing while the page is unloading. If sessionStorage is blocked
      // and we ended up here without a reload, the manual button below
      // gives the user a way out.
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#0E0E0E] text-white">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-white/15 bg-white/10 px-5 py-3 text-sm font-medium hover:bg-white/15"
          >
            Reload
          </button>
        </div>
      );
    }

    return (
      <div className="mx-auto flex min-h-screen max-w-[640px] flex-col items-center justify-center gap-4 bg-[#0E0E0E] px-6 text-center text-white">
        <h1 className="text-2xl font-semibold">Something went wrong rendering this page.</h1>
        <p className="text-sm text-white/60">{String(error.message || error)}</p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-2 rounded-lg border border-white/15 bg-white/10 px-5 py-3 text-sm font-medium hover:bg-white/15"
        >
          Try again
        </button>
      </div>
    );
  }
}
